import React, { useEffect, useState, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../sp-firebase/firebase';
import { useAppSession } from '../contexts/AppSessionContext';
import { IdleTimerProvider } from 'react-idle-timer';
import IdleTimeoutModal from '../components/Auth/IdleTimeoutModal';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * AuthGuard - Protects routes that require authentication
 * Checks: valid session UID + user exists + user is active
 */
const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const { status, user } = useAppSession();

  useEffect(() => {
    // Wait for auth to finish loading
    if (status === 'loading') return;

    // Redirect to login if not authenticated
    if (status === 'unauthenticated' || !user) {
      console.log('🚪 [AUTH GUARD] Not authenticated, redirecting to login');
      navigate('/login');
      return;
    }

    // User is authenticated
    console.log('✅ [AUTH GUARD] Authorized:', user.email);
  }, [status, user, navigate]);

  // Show loading spinner while checking auth
  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Only render children if authenticated
  return status === 'authenticated' && user ? <>{children}</> : null;
};

export default AuthGuard;
